import { useMemo, useState, type ReactNode } from "react";
import type { ApiError } from "../../api/client";
import { formatMoney, sumMoney } from "../../api/money";
import { EMPTY_FILTER, useTracker, type TrackerFilter, type TrackerReport } from "../../api/tracker";
import { PageHead, Stat } from "../../components/ui";
import { useDebounced } from "../../hooks/useDebounced";
import { ColumnPicker } from "./ColumnPicker";
import {
  DEFAULT_VISIBLE,
  EXCLUDING_VAT,
  GROSS_PROFIT,
  INVOICE_NO,
  STATUS,
  SUPPLIER_COST,
  availableColumns,
  browserStore,
  readVisible,
  toggled,
  writeVisible,
  type ColumnStore,
} from "./columns";
import { TrackerFilters } from "./TrackerFilters";
import { TrackerTable, toRecords, type TrackerRecord } from "./TrackerTable";
import "../../styles/tracker.css";

/** The tracker: the client's workbook as a table, over the same filters as the
 * site list, with the Excel download of the same rows. Money columns are
 * absent when the server withheld them; nothing here decides that. */
export default function TrackerPage() {
  const [filter, setFilter] = useState<TrackerFilter>(EMPTY_FILTER);
  const [search, setSearch] = useState("");
  const settled = useDebounced(search);
  const query = useMemo(() => ({ ...filter, q: settled }), [filter, settled]);
  const report = useTracker(query);
  const store = useMemo(browserStore, []);

  return (
    <TrackerView
      report={report.data}
      pending={report.isPending}
      error={report.error}
      store={store}
      toolbar={
        <TrackerFilters
          filter={query}
          search={search}
          options={report.data?.filters}
          onSearch={setSearch}
          onChange={(next) => setFilter({ ...next, q: "" })}
        />
      }
    />
  );
}

/** The KPI strip. "Billed" counts only rows that carry an invoice number:
 * the Excluding VAT column is the BOQ's value whether or not it has been
 * invoiced, and calling an unbilled BOQ billed would overstate the month. */
function Kpis({ records, showMoney }: { records: readonly TrackerRecord[]; showMoney: boolean }) {
  const completed = records.filter((record) => record[STATUS] === "completed").length;
  const billed = records.filter((record) => (record[INVOICE_NO] ?? "") !== "");
  const margin = sumMoney(records.map((record) => record[GROSS_PROFIT]));
  return (
    <div className="stat-strip">
      <Stat label="Completed" value={completed} />
      {showMoney ? (
        <>
          <Stat
            label="Billed excl VAT"
            value={formatMoney(sumMoney(billed.map((record) => record[EXCLUDING_VAT])))}
          />
          <Stat label="Supplier cost" value={formatMoney(sumMoney(records.map((record) => record[SUPPLIER_COST])))} />
          <Stat label="Gross profit" value={formatMoney(margin)} good={Number(margin) > 0} />
        </>
      ) : null}
    </div>
  );
}

/** Everything below the data: the heading with its column picker and
 * download, the KPIs, the toolbar and the table. The column choice is the
 * viewer's own and outlives the visit (`store`); a store that is missing or
 * refuses costs only that memory. */
export function TrackerView({
  report,
  pending,
  error,
  store,
  toolbar,
}: {
  report: TrackerReport | undefined;
  pending: boolean;
  error: ApiError | null;
  store: ColumnStore | null;
  toolbar: ReactNode;
}) {
  const [visible, setVisible] = useState<string[]>(() => readVisible(store));
  // Written only when the viewer chooses, so someone who never touched the
  // picker follows the default if it changes.
  function choose(next: string[]) {
    setVisible(next);
    writeVisible(store, next);
  }

  const available = useMemo(() => availableColumns(report?.columns ?? []), [report]);
  const shown = available.filter((column) => visible.includes(column.key));
  const records = useMemo(() => (report ? toRecords(report.columns, report.rows) : []), [report]);

  return (
    <div className="tracker-page">
      <PageHead
        title="Tracker"
        sub={`${records.length} rows · the client's workbook, live`}
        actions={
          <>
            <ColumnPicker
              columns={available}
              visible={visible}
              onToggle={(key) => choose(toggled(visible, key))}
              onReset={() => choose([...DEFAULT_VISIBLE])}
            />
            {report ? (
              <a className="btn btn-primary" href={report.xlsx_url}>
                Download Excel
              </a>
            ) : (
              <span className="btn btn-primary" aria-disabled="true">
                Download Excel
              </span>
            )}
          </>
        }
      />
      <Kpis records={records} showMoney={report?.show_money ?? false} />
      {toolbar}
      {error ? <p className="notice bad">The tracker could not be loaded: {error.message}</p> : null}
      {pending ? (
        <div className="table-card empty-state">Loading the tracker…</div>
      ) : report && records.length === 0 ? (
        <div className="table-card empty-state">
          <strong>Nothing matches these filters.</strong> Clear a filter or the search to see more sites.
        </div>
      ) : report ? (
        <TrackerTable records={records} columns={shown} />
      ) : null}
      {report && !report.show_money ? (
        <p className="line-note">Financial columns are not shown to this account.</p>
      ) : null}
    </div>
  );
}
